import React, { useState, useEffect } from 'react';
import { usePerformanceMonitoring } from '../hooks/usePerformanceMonitoring';
import './PerformanceDashboard.css';

const PerformanceDashboard = ({ isVisible, onClose }) => {
  const { metrics, getPerformanceData, clearMetrics } = usePerformanceMonitoring();
  const [detailedData, setDetailedData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (isVisible) {
      const data = getPerformanceData();
      setDetailedData(data);
    }
  }, [isVisible, getPerformanceData]);

  if (!isVisible || process.env.NODE_ENV !== 'development') {
    return null;
  }

  const formatDuration = (ms) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getPerformanceStatus = (value, thresholds) => {
    if (value <= thresholds.good) return 'good';
    if (value <= thresholds.ok) return 'ok';
    return 'poor';
  };

  const renderOverview = () => (
    <div className="performance-overview">
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-value">
            {formatDuration(metrics.averageLoadTime)}
          </div>
          <div className="metric-label">Avg Load Time</div>
          <div className={`metric-status ${getPerformanceStatus(metrics.averageLoadTime, { good: 500, ok: 1000 })}`}>
            {metrics.averageLoadTime <= 500 ? '🚀 Fast' : 
             metrics.averageLoadTime <= 1000 ? '⚡ Good' : '🐌 Slow'}
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-value">
            {metrics.cacheHitRate}%
          </div>
          <div className="metric-label">Cache Hit Rate</div>
          <div className={`metric-status ${getPerformanceStatus(100 - metrics.cacheHitRate, { good: 20, ok: 50 })}`}>
            {metrics.cacheHitRate >= 80 ? '💾 Excellent' : 
             metrics.cacheHitRate >= 50 ? '📁 Good' : '🔄 Poor'}
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-value">
            {metrics.errorRate}%
          </div>
          <div className="metric-label">Error Rate</div>
          <div className={`metric-status ${getPerformanceStatus(metrics.errorRate, { good: 1, ok: 5 })}`}>
            {metrics.errorRate <= 1 ? '✅ Excellent' : 
             metrics.errorRate <= 5 ? '⚠️ Acceptable' : '❌ High'}
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-value">
            {metrics.totalRequests}
          </div>
          <div className="metric-label">Total Requests</div>
          <div className="metric-status good">
            📊 Tracked
          </div>
        </div>
      </div>
    </div>
  );

  const renderRecentCalls = () => (
    <div className="recent-calls">
      <h4>Recent API Calls</h4>
      <div className="calls-list">
        {detailedData?.recentCalls?.map((call, index) => (
          <div key={index} className={`call-item ${call.error ? 'error' : ''}`}>
            <div className="call-type">{call.type}</div>
            <div className="call-duration">
              {formatDuration(call.duration)}
              {call.fromCache && <span className="cache-indicator">⚡</span>}
            </div>
            <div className="call-items">{call.itemCount} items</div>
            {call.error && <div className="error-indicator">❌</div>}
          </div>
        ))}
      </div>
    </div>
  );

  const renderErrors = () => (
    <div className="errors-section">
      <h4>Recent Errors</h4>
      {detailedData?.recentErrors?.length === 0 ? (
        <div className="no-errors">✅ No recent errors</div>
      ) : (
        <div className="errors-list">
          {detailedData?.recentErrors?.map((error, index) => (
            <div key={index} className="error-item">
              <div className="error-type">{error.type}</div>
              <div className="error-message">{error.error}</div>
              <div className="error-time">
                {new Date(error.timestamp).toLocaleTimeString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="performance-dashboard-overlay">
      <div className="performance-dashboard">
        <div className="dashboard-header">
          <h3>🚀 Performance Dashboard</h3>
          <div className="header-actions">
            <button 
              className="clear-button"
              onClick={clearMetrics}
              title="Clear all metrics"
            >
              🗑️ Clear
            </button>
            <button 
              className="close-button"
              onClick={onClose}
              title="Close dashboard"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="dashboard-tabs">
          <button 
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab ${activeTab === 'calls' ? 'active' : ''}`}
            onClick={() => setActiveTab('calls')}
          >
            API Calls
          </button>
          <button 
            className={`tab ${activeTab === 'errors' ? 'active' : ''}`}
            onClick={() => setActiveTab('errors')}
          >
            Errors
          </button>
        </div>

        <div className="dashboard-content">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'calls' && renderRecentCalls()}
          {activeTab === 'errors' && renderErrors()}
        </div>

        <div className="dashboard-footer">
          <small>Performance data is only available in development mode</small>
        </div>
      </div>
    </div>
  );
};

// Performance toggle button
export const PerformanceToggle = () => {
  const [isVisible, setIsVisible] = useState(false);

  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <>
      <button 
        className="performance-toggle"
        onClick={() => setIsVisible(!isVisible)}
        title="Toggle Performance Dashboard"
      >
        🚀
      </button>
      <PerformanceDashboard 
        isVisible={isVisible} 
        onClose={() => setIsVisible(false)} 
      />
    </>
  );
};

export default PerformanceDashboard;
